import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary/80 backdrop-blur-xl border border-primary/30 text-primary-foreground hover:bg-primary/90 hover:border-primary/50 shadow-lg shadow-primary/20 transition-all",
        destructive:
          "bg-destructive/80 backdrop-blur-xl border border-destructive/30 text-white hover:bg-destructive/90 hover:border-destructive/50 shadow-lg shadow-destructive/20 transition-all",
        outline:
          "border border-white/10 bg-black/50 backdrop-blur-xl hover:bg-black/60 hover:border-white/20 hover:text-high transition-all",
        secondary:
          "bg-black/50 backdrop-blur-xl border border-white/10 text-high hover:bg-black/60 hover:border-white/20 transition-all",
        ghost:
          "hover:bg-black/30 hover:backdrop-blur-xl hover:text-high transition-all",
        link: "text-primary underline-offset-4 hover:underline",
        success:
          "bg-green-600/80 backdrop-blur-xl border border-green-500/30 text-white hover:bg-green-600/90 hover:border-green-500/50 shadow-lg shadow-green-600/20 transition-all",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
